import React, { useState, useEffect, useMemo, useRef } from "react";
import FullScreenLoader from "../Components/FullScreenLoader"; // Loader component

export default function ClubMatches({ selectedClub, selectedDate, setLoadingState }) {
  const [matchesData, setMatchesData] = useState([]);
  const [loading, setLoading] = useState(true); // Internal loader state

  // Stable URL for deps
  const club_schedule_api_url = useMemo(
    () =>
      `https://us-central1-hsf-production-505a6.cloudfunctions.net/webApi/clubs/${selectedClub}/matches?from=${selectedDate}`,
    [selectedClub, selectedDate]
  );

  // Keep refs for timeout and a guard so we don't "stop" twice
  const stopTimeoutRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;   // prevents double stop
    let started = false;   // did we call setLoadingState(true)?
    const stop = () => {
      if (stopped) return;
      stopped = true;
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      setLoading(false);
      if (started) setLoadingState(false);
    };

    const fetchData = async () => {
      // START (always balance this)
      setLoading(true);
      setLoadingState(true);
      started = true;

      try {
        const response = await fetch(club_schedule_api_url, { signal: controller.signal });
        // If the request was aborted mid-flight, this will throw AbortError.
        const data = await response.json();
        setMatchesData(Array.isArray(data?.schedule) ? data.schedule : []);
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Error fetching data:", error);
        }
        // If it was an AbortError, we still want to STOP in cleanup.
      } finally {
        // Smooth transition; if unmounted/switching fast, cleanup will clear this
        stopTimeoutRef.current = setTimeout(() => {
          stop();
        }, 300);
      }
    };

    fetchData();

    // Cleanup ALWAYS balances the START above:
    // - abort fetch
    // - clear any pending timeout
    // - ensure loader stops if finally didn't get a chance
    return () => {
      controller.abort();
      stop();
    };
  }, [club_schedule_api_url, setLoadingState]);

  const convertDate = (seconds) => {
    const date = new Date(seconds * 1000);
    return date.toLocaleDateString("fo-FO", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  let previousDate = null;

  return (
    <>
      {loading && <FullScreenLoader />}

      <section className="venue-matches club-matches">
        <div>
          <div>
            <div>
              <table className="table">
                <tbody>
                  {matchesData.map((match) => {
                    const currentDate = convertDate(match.date._seconds);
                    const shouldCreateNewTable = currentDate !== previousDate;
                    previousDate = currentDate;

                    return (
                      <React.Fragment key={match.id}>
                        {shouldCreateNewTable && (
                          <tr className="vendate">
                            <td colSpan="7" className="venue-date">
                              {currentDate}
                            </td>
                          </tr>
                        )}

                        <tr
                          title="heinta dómaraseðil"
                          className={match.matchReport ? "hover played" : ""}
                          onClick={() => {
                            if (match.matchReport) {
                              window.open(match.matchReport, "_blank");
                            }
                          }}
                        >
                          <td className="left-align venue-clock">
                            {match.time}
                            <div className="justsmall">
                              <div className="group">{match.group.name}</div>
                              <div className="venue">{match.venue.name}</div>
                            </div>
                          </td>
                          <td className="left-align venue-league">
                            <div className="matchGroupName">{match.group.name}</div>
                            <div className="matchVenueName">
                              {match.venue.name}
                              {match.matchReport && (
                                <img
                                  title="heinta dómaraseðil"
                                  className="pdfimg"
                                  alt=""
                                  src="/pdf.png"
                                />
                              )}
                            </div>
                          </td>
                          <td className="right-align match-team-name">{match.homeTeam.name}</td>
                          <td className="match-team-logo center-align venue-team-logo">
                            <img
                              src={`https://files.hsf.fo/clubs/${match.homeTeam.clubId}.webp`}
                              alt="Team 1 Logo"
                            />
                          </td>
                          <td className="dash">
                            {match.result?.home.goalsFor}-{match.result?.away.goalsFor}
                          </td>
                          <td className="match-team-logo center-align venue-team-logo">
                            <img
                              src={`https://files.hsf.fo/clubs/${match.awayTeam.clubId}.webp`}
                              alt="Team 2 Logo"
                            />
                          </td>
                          <td className="left-align match-team-name">{match.awayTeam.name}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
